import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Building2, FolderKanban, UserRound, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCommandPalette } from '@/stores/commandPalette';
import { useGlobalSearch } from '../queries';
import type { SearchHit, SearchHitType } from '../types';

const TYPE_META: Record<SearchHitType, { label: string; icon: LucideIcon; to: (id: number) => string }> = {
  company: { label: 'Companies', icon: Building2, to: (id) => `/crm/companies/${id}` },
  contact: { label: 'Contacts', icon: UserRound, to: (id) => `/crm/contacts/${id}` },
  deal: { label: 'Deals', icon: Briefcase, to: (id) => `/crm/deals/${id}` },
  project: { label: 'Projects', icon: FolderKanban, to: (id) => `/projects/${id}` },
  employee: { label: 'Team', icon: Users, to: (id) => `/hrm/employees/${id}` },
};

const TYPE_ORDER: SearchHitType[] = ['company', 'contact', 'deal', 'project', 'employee'];

function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function CommandPalette() {
  const open = useCommandPalette((s) => s.open);
  const setOpen = useCommandPalette((s) => s.setOpen);
  const toggle = useCommandPalette((s) => s.toggle);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const { data, isFetching } = useGlobalSearch(open ? debounced : '');

  // ⌘K / Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const grouped = useMemo(() => {
    const byType = new Map<SearchHitType, SearchHit[]>();
    for (const hit of data ?? []) {
      const bucket = byType.get(hit.type);
      if (bucket) bucket.push(hit);
      else byType.set(hit.type, [hit]);
    }
    return TYPE_ORDER.filter((t) => byType.has(t)).map((t) => [t, byType.get(t)!] as const);
  }, [data]);

  function go(hit: SearchHit) {
    setOpen(false);
    navigate(TYPE_META[hit.type].to(hit.id));
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery(''); // reopen fresh
  }

  const hasQuery = debounced.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader className="sr-only">
        <DialogTitle>Search</DialogTitle>
        <DialogDescription>
          Search companies, contacts, deals, projects and team.
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="overflow-hidden p-0" showCloseButton={false}>
        {/* Server-side search → disable cmdk's client filtering. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search companies, deals, projects, people…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {!hasQuery ? (
              <div className="text-muted-foreground p-4 text-sm">
                Type to search across the workspace.
              </div>
            ) : isFetching && !grouped.length ? (
              <div className="text-muted-foreground p-4 text-sm">Searching…</div>
            ) : (
              <CommandEmpty>No results for “{debounced}”.</CommandEmpty>
            )}
            {grouped.map(([type, hits]) => {
              const meta = TYPE_META[type];
              const Icon = meta.icon;
              return (
                <CommandGroup key={type} heading={meta.label}>
                  {hits.map((hit) => (
                    <CommandItem
                      key={`${hit.type}-${hit.id}`}
                      value={`${hit.type}-${hit.id}-${hit.label}`}
                      onSelect={() => go(hit)}
                    >
                      <Icon className="size-4" />
                      {hit.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
